/**
 * Client-side error logger utility.
 * Sends structured error data from the browser to /api/log-error.
 *
 * Used by:
 * - ErrorPage.tsx / ErrorBoundary.tsx / GlobalErrorListener.tsx (CSR crashes)
 * - React Query onError handlers, via reportUserFacingFailure — which decides
 *   whether the failure was the backend's, the network's, or our own JS.
 */

import {
  type BackendApiErrorData,
  type ClientSideErrorData,
  type ErrorReportMeta,
  ErrorType,
  FailureKind,
} from "./errorTypes";

/**
 * Log a client-side (CSR) error — crashes caught by React error boundaries.
 */
export async function logClientSideError(
  error: Error & { digest?: string },
  componentStack?: string,
): Promise<void> {
  const data: ClientSideErrorData = {
    type: ErrorType.CLIENT_SIDE,
    errorMessage: error.message,
    errorName: error.name,
    stack: error.stack || "",
    componentTree: componentStack || undefined,
    routePath: window.location.pathname,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    host: window.location.hostname,
    url: window.location.href,
  };

  await sendToLogEndpoint(data);
}

/**
 * React Query funnels a throw from `onSuccess` into `onError`, so a plain JS
 * exception reaches the same handler as a genuine API failure. Tell them apart.
 */
export function classifyFailure(error: any): FailureKind {
  if (error?.response) return FailureKind.API_HTTP;
  if (error?.request || error?.isAxiosError) return FailureKind.API_NETWORK;
  return FailureKind.FRONTEND_EXCEPTION;
}

/** Short code shown in the error popup, searchable as `_error_ref` in Graylog. */
export function newErrorRef(): string {
  const random = Math.random().toString(36).slice(2, 6);
  const time = Date.now().toString(36).slice(-4);
  return `${time}${random}`.toUpperCase();
}

const stringifyCapped = (value: unknown, cap = 1000): string | undefined => {
  if (value === undefined || value === null) return undefined;
  try {
    const raw = typeof value === "string" ? value : JSON.stringify(value);
    return raw?.slice(0, cap);
  } catch {
    return "[Could not serialize]";
  }
};

/**
 * Report a failure the customer actually saw. Returns the ref synchronously so
 * the caller can show it; the send is fire-and-forget so it never blocks the UI.
 */
export function reportUserFacingFailure(params: {
  error: any;
  context: string;
  extra?: ErrorReportMeta["extra"];
  errorRef?: string;
}): string {
  const { error, context, extra } = params;
  const errorRef = params.errorRef ?? newErrorRef();

  if (typeof window === "undefined") return errorRef;

  const failureKind = classifyFailure(error);
  const meta: ErrorReportMeta = {
    failureKind,
    errorRef,
    context,
    extra,
    forceLog: true,
  };

  const data: BackendApiErrorData | ClientSideErrorData =
    failureKind === FailureKind.FRONTEND_EXCEPTION
      ? {
          ...meta,
          type: ErrorType.CLIENT_SIDE,
          errorMessage: error?.message || String(error) || "Unknown client error",
          errorName: error?.name || "Error",
          stack: error?.stack || "",
          routePath: window.location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          host: window.location.hostname,
          url: window.location.href,
        }
      : {
          ...meta,
          type: ErrorType.BACKEND_API,
          endpoint:
            error?.response?.config?.url || error?.config?.url || "unknown",
          method: (
            error?.response?.config?.method ||
            error?.config?.method ||
            "unknown"
          ).toUpperCase(),
          statusCode: error?.response?.status || error?.status || 0,
          responseBody: stringifyCapped(error?.response?.data),
          requestPayload: stringifyCapped(error?.config?.data),
          errorMessage:
            error?.message ||
            stringifyCapped(error?.response?.data?.message, 200) ||
            "Unknown API error",
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          host: window.location.hostname,
          url: window.location.href,
        };

  void sendToLogEndpoint(data).catch(() => {});

  return errorRef;
}

/**
 * Internal: send structured error data to the log-error API route.
 */
async function sendToLogEndpoint(
  data: ClientSideErrorData | BackendApiErrorData,
): Promise<void> {
  try {
    const body = JSON.stringify(data);

    // In-app browsers tear the page down mid-request; sendBeacon survives it.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const queued = navigator.sendBeacon(
        "/api/log-error",
        new Blob([body], { type: "application/json" }),
      );
      if (queued) return;
    }

    const response = await fetch("/api/log-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });

    if (!response.ok) {
      console.error("[ErrorLogger] Failed to send error log:", response.status);
    }
  } catch (err) {
    // Silently fail — don't crash the app because logging failed
    console.error("[ErrorLogger] Network error sending log:", err);
  }
}
